/**
 * Тексты, которые бот пишет в каналах: панели, приветствия, объявления.
 *
 * Устроено так же, как личные сообщения: реестр описаний, пустой текст
 * означает «взять исходный», и новый текст появляется в коде без миграции.
 * Разница в том, что здесь нет выключателей — панель без текста не бывает.
 *
 * В отличие от личных сообщений, строка с пустой подстановкой отсюда не
 * выпадает: у этих текстов подстановки есть всегда, а исчезнувшая строка в
 * панели выглядела бы поломкой, а не задумкой.
 */

import type { NotificationPlaceholder } from './notifications.js';

export type MessageId =
  | 'panel.profile.title'
  | 'panel.profile.body'
  | 'panel.profile.button'
  | 'panel.family.title'
  | 'panel.family.body'
  | 'panel.family.button'
  | 'panel.admin.title'
  | 'panel.admin.body'
  | 'panel.admin.button'
  | 'channel.rollbackIntro'
  | 'channel.welcome'
  | 'event.announceTitle'
  | 'event.joinButton'
  | 'event.leaveButton'
  | 'event.leftMain'
  | 'event.leftReserve';

/** К чему относится текст — по этим группам он разложен в админке. */
export type MessageGroup = 'panels' | 'channels' | 'events';

export const MESSAGE_GROUP_LABEL: Readonly<Record<MessageGroup, string>> = {
  panels: 'Постоянные панели',
  channels: 'Каналы',
  events: 'Наборы',
};

/**
 * Пределы Discord.
 *
 * Превысить их нельзя: Discord отклонит сообщение целиком, и панель
 * останется на прежнем тексте либо не отправится вовсе.
 */
const EMBED_TITLE_LIMIT = 256;
const EMBED_DESCRIPTION_LIMIT = 4096;
const BUTTON_LABEL_LIMIT = 80;
const CONTENT_LIMIT = 2000;

export interface MessageDefinition {
  id: MessageId;
  group: MessageGroup;
  label: string;
  description: string;
  placeholders: NotificationPlaceholder[];
  defaultText: string;
  /** Многострочный ли текст — от этого зависит поле ввода в админке. */
  multiline: boolean;
  maxLength: number;
}

/**
 * Описание всех текстов.
 *
 * Исходные тексты повторяют прежние слово в слово: смысл изменения — дать
 * возможность их править, а не переписать за сообщество его формулировки.
 */
export const MESSAGES: readonly MessageDefinition[] = [
  {
    id: 'panel.profile.title',
    group: 'panels',
    label: 'Панель анкеты — заголовок',
    description: 'Постоянное сообщение в канале регистрации профилей.',
    placeholders: [],
    defaultText: 'Анкета участника',
    multiline: false,
    maxLength: EMBED_TITLE_LIMIT,
  },
  {
    id: 'panel.profile.body',
    group: 'panels',
    label: 'Панель анкеты — текст',
    description: 'Что человек читает перед тем, как нажать кнопку.',
    placeholders: [],
    defaultText: [
      'Заполните профиль один раз, чтобы участвовать в наборах.',
      '',
      '**Нужно указать:**',
      '• Игровой ник в формате Имя_Фамилия',
    ].join('\n'),
    multiline: true,
    maxLength: EMBED_DESCRIPTION_LIMIT,
  },
  {
    id: 'panel.profile.button',
    group: 'panels',
    label: 'Панель анкеты — кнопка',
    description: 'Надпись на кнопке, открывающей форму анкеты.',
    placeholders: [],
    defaultText: 'Заполнить анкету',
    multiline: false,
    maxLength: BUTTON_LABEL_LIMIT,
  },
  {
    id: 'panel.family.title',
    group: 'panels',
    label: 'Панель заявки — заголовок',
    description: 'Постоянное сообщение в канале подачи заявок в семью.',
    placeholders: [],
    defaultText: 'Заявка в семью',
    multiline: false,
    maxLength: EMBED_TITLE_LIMIT,
  },
  {
    id: 'panel.family.body',
    group: 'panels',
    label: 'Панель заявки — текст',
    description: 'Что нужно подготовить до нажатия кнопки.',
    placeholders: [],
    defaultText: [
      'Нажмите кнопку ниже и заполните короткую анкету.',
      '',
      '**Что нужно указать:**',
      '• Игровой ник',
      '• Возраст',
      '• Откаты',
      '• Адекватность по 10-балльной шкале',
    ].join('\n'),
    multiline: true,
    maxLength: EMBED_DESCRIPTION_LIMIT,
  },
  {
    id: 'panel.family.button',
    group: 'panels',
    label: 'Панель заявки — кнопка',
    description: 'Надпись на кнопке, открывающей форму заявки.',
    placeholders: [],
    defaultText: 'Создать заявку',
    multiline: false,
    maxLength: BUTTON_LABEL_LIMIT,
  },
  {
    id: 'panel.admin.title',
    group: 'panels',
    label: 'Админ-панель — заголовок',
    description: 'Единственное сообщение в канале админ-панели.',
    placeholders: [],
    defaultText: 'Админ-панель',
    multiline: false,
    maxLength: EMBED_TITLE_LIMIT,
  },
  {
    id: 'panel.admin.body',
    group: 'panels',
    label: 'Админ-панель — текст',
    description: 'Кнопка со ссылкой на админку добавляется сама, если админка включена.',
    placeholders: [],
    defaultText: [
      'Управление наборами, составами, заявками и откатами — в веб-админке.',
      '',
      'Вход по кнопке ниже, через Discord.',
    ].join('\n'),
    multiline: true,
    maxLength: EMBED_DESCRIPTION_LIMIT,
  },
  {
    id: 'panel.admin.button',
    group: 'panels',
    label: 'Админ-панель — кнопка',
    description: 'Надпись на кнопке-ссылке.',
    placeholders: [],
    defaultText: 'Открыть админку',
    multiline: false,
    maxLength: BUTTON_LABEL_LIMIT,
  },
  {
    id: 'channel.rollbackIntro',
    group: 'channels',
    label: 'Приветствие в личном канале откатов',
    description:
      'Первое сообщение в личном канале. Уже созданные каналы бот правит фоном, новое сообщение туда не шлётся.',
    placeholders: [],
    defaultText: [
      '**Это ваш личный канал для откатов.**',
      '',
      'После мероприятия скидывайте сюда ссылку на запись с YouTube — одним сообщением, просто ссылкой.',
      'Всё присланное видно ростер-менеджерам. Чем больше откатов, тем быстрее поднимается ваш tier.',
      '',
      'Канал закрытый: его видите только вы, модераторы и бот.',
    ].join('\n'),
    multiline: true,
    maxLength: CONTENT_LIMIT,
  },
  {
    id: 'channel.welcome',
    group: 'channels',
    label: 'Приветствие новичка',
    description: 'Уходит в канал «Добро пожаловать», когда человек заходит на сервер.',
    placeholders: [{ key: 'mention', description: 'Упоминание вошедшего' }],
    defaultText: 'Добро пожаловать, {mention}!',
    multiline: true,
    maxLength: CONTENT_LIMIT,
  },
  {
    id: 'event.announceTitle',
    group: 'events',
    label: 'Заголовок карточки набора',
    description: 'Заголовок анонса в канале плюсов.',
    placeholders: [{ key: 'eventType', description: 'Тип мероприятия, например MCL' }],
    defaultText: 'Набор | {eventType}',
    multiline: false,
    maxLength: EMBED_TITLE_LIMIT,
  },
  {
    id: 'event.joinButton',
    group: 'events',
    label: 'Кнопка записи',
    description: 'Надпись на кнопке, которой человек записывается в набор.',
    placeholders: [],
    defaultText: 'КИНУТЬ ПЛЮС',
    multiline: false,
    maxLength: BUTTON_LABEL_LIMIT,
  },
  {
    id: 'event.leaveButton',
    group: 'events',
    label: 'Кнопка выхода',
    description: 'Надпись на кнопке, которой человек убирает плюс.',
    placeholders: [],
    defaultText: 'УБРАТЬ ПЛЮС',
    multiline: false,
    maxLength: BUTTON_LABEL_LIMIT,
  },
  {
    id: 'event.leftMain',
    group: 'events',
    label: 'Объявление об уходе из основы',
    description: 'Отправляется в канал плюсов, если объявления включены на вкладке «Наборы».',
    placeholders: [
      { key: 'mention', description: 'Упоминание ушедшего — ссылка, без уведомления' },
      { key: 'nick', description: 'Игровой ник' },
      { key: 'static', description: 'Статик' },
      { key: 'count', description: 'Сколько осталось в основном составе' },
      { key: 'capacity', description: 'Вместимость основного состава' },
    ],
    defaultText: [
      '❌ {mention} — **{nick}** убрал плюс.',
      'Основной состав: **{count}/{capacity}** — место свободно.',
    ].join('\n'),
    multiline: true,
    maxLength: CONTENT_LIMIT,
  },
  {
    id: 'event.leftReserve',
    group: 'events',
    label: 'Объявление об уходе из замены',
    description: 'По умолчанию выключено — включается на вкладке «Наборы».',
    placeholders: [
      { key: 'mention', description: 'Упоминание ушедшего — ссылка, без уведомления' },
      { key: 'nick', description: 'Игровой ник' },
      { key: 'static', description: 'Статик' },
    ],
    defaultText: '❌ {mention} — **{nick}** убрал плюс из замены.',
    multiline: true,
    maxLength: CONTENT_LIMIT,
  },
];

/** Тексты сообщества: идентификатор — текст. Пустой означает «исходный». */
export type MessageSettings = Partial<Record<MessageId, string>>;

export const DEFAULT_MESSAGES: MessageSettings = Object.fromEntries(
  MESSAGES.map((definition) => [definition.id, definition.defaultText]),
);

export function messageDefinition(id: MessageId): MessageDefinition {
  const found = MESSAGES.find((definition) => definition.id === id);
  if (found === undefined) {
    throw new Error(`Текст ${id} не описан`);
  }

  return found;
}

/**
 * Текст сообщества либо исходный.
 *
 * Пустое значение — это «взять исходный», а не «ничего не писать»: пустая
 * панель или пустое сообщение в канале выглядели бы поломкой бота.
 */
export function messageText(settings: MessageSettings | undefined, id: MessageId): string {
  const stored = settings?.[id];
  return stored === undefined || stored.trim() === '' ? messageDefinition(id).defaultText : stored;
}

const PLACEHOLDER = /\{([a-zA-Z]+)\}/g;

/** Подставляет значения в текст. Неизвестная подстановка остаётся как есть. */
export function renderMessage(text: string, values: Readonly<Record<string, string>> = {}): string {
  return text.replaceAll(PLACEHOLDER, (whole, key: string) => values[key] ?? whole);
}

/** Готовый текст сообщества с подставленными значениями. */
export function renderMessageById(
  settings: MessageSettings | undefined,
  id: MessageId,
  values: Readonly<Record<string, string>> = {},
): string {
  return renderMessage(messageText(settings, id), values);
}

/**
 * Похоже ли значение на набор текстов.
 *
 * Как и у личных сообщений, набор может быть неполным: недостающее
 * достраивается исходным текстом при чтении.
 */
export function isMessageSettings(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return Object.values(value as Record<string, unknown>).every((item) => typeof item === 'string');
}
